/* lp-colour-palette — <lp-colour-palette>: swatch row over colour cards. A swatch or card tap highlights that colour
   (the card grows and glows, the rest step back; on mobile the row slides it to the centre). On mobile the card in
   the middle of the row is always the highlighted one: swiping moves the highlight and the swatch ring with it, no
   tap needed. "Shop now" emits lp:colour-select {colour, handle, source: 'lp-colour-palette'} and scrolls to
   data-target (default "product" → the set switcher or buy box). Follows lp:colour-select from other sections so the
   highlight stays in sync.
   Desktop first view, with GSAP (LP.gsap, core only) and motion allowed: the cards start stacked at the centre like
   a hand of cards and fan out into the grid, the swatches pop in. Without GSAP the base fade-up runs instead.
   Colour tint (root .lp-colour-palette--tint): the section takes the picked colour's hue (the swatch's data-lp-tint);
   on screen, with GSAP and motion allowed, the new tint sweeps out from the tap in a soft-edged circle, the same
   sweep as lp-set-switcher. A centre pick on the mobile row waits until the row rests, so a fling doesn't flash
   through the colours. Off screen, without GSAP or with reduced motion, the tint changes at once.
   Galleries: each card's picture is a snap-scroll row of that colour's photos; the centred slide lights its page dot,
   a dot tap scrolls to that photo. Without JS the "Shop now" links are plain anchors to the target. */
(function () {
  'use strict';
  if (window.customElements.get('lp-colour-palette')) return;

  var FEATHER = 40; /* px: the soft edge of the sweep, the same 2.5rem as the tint gradient in the CSS */
  function norm(v) { return String(v == null ? '' : v).trim().toLowerCase(); }
  function motionOK() { return window.LP ? window.LP.motionOK : !window.matchMedia('(prefers-reduced-motion: reduce)').matches; }

  class LpColourPalette extends HTMLElement {
    connectedCallback() {
      var self = this;
      this.track = this.querySelector('[data-lp-track]');
      this.tintEl = this.querySelector('.lp-colour-palette__tint');
      var picked = this.querySelector('.lp-colour-palette__card.is-selected');
      this.tintShown = picked ? norm(picked.dataset.lpColour) : '';
      this.onClick = function (e) {
        var dot = e.target.closest('[data-lp-slide]');
        if (dot && self.contains(dot)) {
          var gal = dot.closest('.lp-colour-palette__card').querySelector('[data-lp-gallery]'), slide = gal && gal.children[parseInt(dot.dataset.lpSlide, 10)];
          if (slide) gal.scrollTo({ left: slide.offsetLeft, behavior: motionOK() ? 'smooth' : 'auto' });
          return;
        }
        var pick = e.target.closest('[data-lp-pick]');
        if (pick && self.contains(pick)) { e.preventDefault(); self.select(pick.dataset.lpPick, { pulse: true, slide: true, origin: pick }); return; }
        var shop = e.target.closest('[data-lp-shop]');
        if (shop && self.contains(shop)) {
          var colour = shop.dataset.lpShop;
          self.select(colour, { pulse: false, slide: false, origin: shop });
          if (!window.LP) return;
          window.LP.emit('colour-select', { colour: colour, handle: self.dataset.handle || '', source: 'lp-colour-palette' });
          var target = window.LP.resolveTarget(self.dataset.target || 'product');
          if (target) { e.preventDefault(); window.LP.scrollTo(target); }
        }
      };
      this.addEventListener('click', this.onClick);
      if (window.LP) {
        this.off = window.LP.on('colour-select', function (e) {
          var d = e.detail || {};
          if (!d.colour || d.source === 'lp-colour-palette') return;
          self.select(d.colour, { pulse: false, slide: true });
        });
      }
      /* theme editor: selecting a Colour block highlights it */
      this.onBlockSelect = function (e) {
        var el = e.target && e.target.closest ? e.target.closest('[data-lp-colour]') : null;
        if (el && self.contains(el)) self.select(el.dataset.lpColour, { pulse: true, slide: true, origin: el });
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      this.watchCentre();
      this.watchScreen();
      this.watchGalleries();
      /* GSAP for the fan-in: fetched when the section nears the screen; plays only if it arrived before the fade-in */
      if (window.LP && window.LP.near && window.LP.gsap && this.dataset.lpGsap) {
        this.offNear = window.LP.near(this, function () {
          window.LP.gsap([self.dataset.lpGsap]).then(function (g) {
            if (!g || !self.isConnected) return;
            self.g = g;
            if (!self.classList.contains('is-visible')) self.armed = true;
          });
        });
      }
      if (window.MutationObserver) {
        this.mo = new MutationObserver(function () {
          if (!self.classList.contains('is-visible')) return;
          self.mo.disconnect(); self.mo = null;
          if (self.armed && self.g && window.matchMedia('(min-width: 48rem)').matches) self.fanIn();
        });
        this.mo.observe(this, { attributes: true, attributeFilter: ['class'] });
      }
    }
    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      if (this.off) this.off();
      if (this.offNear) this.offNear();
      if (this.mo) this.mo.disconnect();
      if (this.centreIO) this.centreIO.disconnect();
      if (this.screenIO) this.screenIO.disconnect();
      (this.galleryIOs || []).forEach(function (io) { io.disconnect(); });
      if (this.track && this.onSlid) this.track.removeEventListener('scrollend', this.onSlid);
      if (this.fanTl) this.fanTl.kill();
      if (this.tintTl) this.tintTl.kill();
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      clearTimeout(this.pulseTimer);
      clearTimeout(this.slideTimer);
      clearTimeout(this.tintTimer);
    }
    /* on screen: a tint change sweeps; off screen it just changes */
    watchScreen() {
      var self = this;
      if (!this.tintEl || !('IntersectionObserver' in window)) { this.onScreen = true; return; }
      this.screenIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { self.onScreen = e.isIntersecting; });
      }, { threshold: 0 });
      this.screenIO.observe(this);
    }
    /* mobile row: a thin band down the middle of the row finds the centred card and highlights it (the swatch ring
       follows). Only while the row scrolls sideways (the desktop grid is left alone), and not while a tap is sliding
       the row to its card, so the highlight doesn't flick through the colours in between. */
    watchCentre() {
      var t = this.track, self = this;
      if (!t || !('IntersectionObserver' in window)) return;
      this.centreIO = new IntersectionObserver(function (entries) {
        if (self.sliding || t.scrollWidth <= t.clientWidth + 2) return;
        var hit = entries.filter(function (e) { return e.isIntersecting; }).pop();
        if (hit && !hit.target.classList.contains('is-selected')) self.select(hit.target.dataset.lpColour, { centre: true, origin: hit.target });
      }, { root: t, rootMargin: '0px -49% 0px -49%', threshold: 0 });
      Array.prototype.forEach.call(t.children, function (c) { self.centreIO.observe(c); });
      this.onSlid = function () { clearTimeout(self.slideTimer); self.sliding = false; };
      t.addEventListener('scrollend', this.onSlid);
    }
    /* each card's gallery: the slide in the middle lights its page dot */
    watchGalleries() {
      var self = this;
      this.galleryIOs = [];
      Array.prototype.forEach.call(this.querySelectorAll('[data-lp-gallery]'), function (gal) {
        var card = gal.closest('.lp-colour-palette__card'), dots = card ? card.querySelectorAll('[data-lp-slide]') : [];
        if (!dots.length || !('IntersectionObserver' in window)) return;
        var io = new IntersectionObserver(function (entries) {
          var hit = entries.filter(function (e) { return e.isIntersecting; }).pop();
          if (!hit) return;
          var k = Array.prototype.indexOf.call(gal.children, hit.target);
          Array.prototype.forEach.call(dots, function (d, i) { d.classList.toggle('is-active', i === k); d.setAttribute('aria-selected', i === k ? 'true' : 'false'); });
        }, { root: gal, rootMargin: '0px -49% 0px -49%', threshold: 0 });
        Array.prototype.forEach.call(gal.children, function (li) { io.observe(li); });
        self.galleryIOs.push(io);
      });
    }
    /* the cards start stacked at the centre, turned like a hand of cards, and fan out from the middle */
    fanIn() {
      var g = this.g, track = this.track, self = this;
      var cards = track ? Array.prototype.slice.call(track.children) : [];
      var swatches = Array.prototype.slice.call(this.querySelectorAll('.lp-colour-palette__swatch'));
      if (cards.length < 2) return;
      var tr = track.getBoundingClientRect(), cx = tr.left + tr.width / 2, mid = (cards.length - 1) / 2;
      var offsets = cards.map(function (c) { var r = c.getBoundingClientRect(); return cx - (r.left + r.width / 2); });
      var all = cards.concat(swatches);
      g.set(all, { transition: 'none' });
      this.fanTl = g.timeline({ onComplete: function () { g.set(all, { clearProps: 'transition,transform,opacity' }); self.fanTl = null; } })
        .from(cards, {
          x: function (i) { return offsets[i] * 0.92; }, y: 48, rotation: function (i) { return (i - mid) * 6; }, scale: 0.86, opacity: 0,
          duration: 1.15, ease: 'power4.out', stagger: { each: 0.08, from: 'center' }
        }, 0)
        .from(swatches, { scale: 0, opacity: 0, duration: 0.55, ease: 'back.out(2.2)', stagger: 0.06 }, 0.2);
    }
    select(colour, opts) {
      opts = opts || {};
      var key = norm(colour), card = null;
      Array.prototype.forEach.call(this.querySelectorAll('.lp-colour-palette__swatch'), function (b) {
        b.setAttribute('aria-pressed', norm(b.dataset.lpPick) === key ? 'true' : 'false');
      });
      Array.prototype.forEach.call(this.querySelectorAll('.lp-colour-palette__card'), function (c) {
        var on = norm(c.dataset.lpColour) === key;
        c.classList.toggle('is-selected', on);
        if (on) card = c;
      });
      if (!card) return;
      if (this.fanTl) this.fanTl.progress(1);
      this.setAttribute('data-has-selection', '');
      this.tint(colour, opts);
      if (opts.pulse && motionOK()) {
        var prev = this.querySelector('.lp-colour-palette__card.is-pulse'); if (prev) prev.classList.remove('is-pulse');
        void card.offsetWidth; /* restart the animation when the same card is picked twice */
        card.classList.add('is-pulse');
        clearTimeout(this.pulseTimer);
        this.pulseTimer = setTimeout(function () { card.classList.remove('is-pulse'); }, 950);
      }
      var t = this.track, self = this;
      if (opts.slide && t && t.scrollWidth > t.clientWidth + 2) {
        var left = Math.max(0, Math.min(t.scrollWidth - t.clientWidth, card.offsetLeft - (t.clientWidth - card.offsetWidth) / 2));
        if (Math.abs(left - t.scrollLeft) < 2) return;
        /* the centre band waits until the slide lands (scrollend, or a timeout where scrollend isn't supported) */
        this.sliding = true;
        clearTimeout(this.slideTimer);
        this.slideTimer = setTimeout(function () { self.sliding = false; }, 900);
        t.scrollTo({ left: left, behavior: motionOK() ? 'smooth' : 'auto' });
      }
    }
    /* the section tint follows the picked colour: a soft-edged circle of the new tint grows from the tap (opts.origin,
       else the colour's swatch) until it covers the section, then becomes the base. A second pick first finishes the
       sweep in flight, so every sweep starts from a settled colour. */
    tint(colour, opts) {
      if (!this.tintEl) return;
      var self = this, key = norm(colour);
      opts = opts || {};
      clearTimeout(this.tintTimer);
      if (opts.centre) { this.tintTimer = setTimeout(function () { self.tint(colour, { origin: opts.origin }); }, 220); return; }
      if (key === this.tintShown) return;
      this.tintShown = key;
      var hex = this.tintFor(key), g = this.g;
      if (this.tintTl) this.tintTl.progress(1);
      if (!g || !motionOK() || !this.onScreen) { this.setTint(hex); return; }
      var box = this.getBoundingClientRect(), o = this.originOf(opts.origin, key);
      var ox = o.x - box.left, oy = o.y - box.top;
      var R = Math.max(Math.hypot(ox, oy), Math.hypot(box.width - ox, oy), Math.hypot(ox, box.height - oy), Math.hypot(box.width - ox, box.height - oy)) + FEATHER;
      var el = this.tintEl, proxy = { r: 0 };
      this.style.setProperty('--lp-colour-palette-tint-next', hex || 'var(--s-bg)');
      el.style.setProperty('--lp-colour-palette-wx', ox + 'px');
      el.style.setProperty('--lp-colour-palette-wy', oy + 'px');
      el.style.setProperty('--lp-colour-palette-wr', '0px');
      this.tintTl = g.to(proxy, {
        r: R, duration: Math.min(1.2, Math.max(0.8, R / 1500)), ease: 'power2.inOut',
        onUpdate: function () { el.style.setProperty('--lp-colour-palette-wr', proxy.r + 'px'); },
        onComplete: function () { self.tintTl = null; self.setTint(hex); }
      });
    }
    setTint(hex) {
      if (hex) this.style.setProperty('--lp-colour-palette-tint', hex); else this.style.removeProperty('--lp-colour-palette-tint');
      this.style.removeProperty('--lp-colour-palette-tint-next');
      if (this.tintEl) this.tintEl.style.setProperty('--lp-colour-palette-wr', '0px');
    }
    tintFor(key) { var sw = this.swatchFor(key); return sw ? sw.dataset.lpTint || '' : ''; }
    swatchFor(key) {
      var list = this.querySelectorAll('.lp-colour-palette__swatch');
      for (var i = 0; i < list.length; i++) { if (norm(list[i].dataset.lpPick) === key) return list[i]; }
      return null;
    }
    /* the sweep's centre, in viewport px */
    originOf(el, key) {
      el = el && el.getBoundingClientRect ? el : (this.swatchFor(key) || this);
      var r = el.getBoundingClientRect();
      return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
    }
  }
  window.customElements.define('lp-colour-palette', LpColourPalette);
})();
